import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import {
  ShieldCheck, Plus, Check, X, AlertTriangle, ToggleLeft, ToggleRight,
  FlaskConical, Users, Plug,
} from 'lucide-react';
import AccessRulesTab from './access-control/AccessRulesTab';
import RoleMappingTab from './access-control/RoleMappingTab';
import IntegrationsTab from './access-control/IntegrationsTab';
import SsoAttemptsLogTab from './access-control/SsoAttemptsLogTab';

export default function AccessControl() {
  const [activeTab, setActiveTab]     = useState('access');   // 'access' | 'rolemapping' | 'sso-log' | 'integrations'
  const [config, setConfig]           = useState({ enabled: false, token_fields: [], operators: [], portal_roles: [] });
  const [rules, setRules]             = useState([]);
  const [mappingRules, setMappingRules] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [toggling, setToggling]       = useState(false);
  const [showTest, setShowTest]       = useState(false);
  const [testProps, setTestProps]     = useState({ email: '', user_role: '', domain_type: '', country: '' });
  const [testResult, setTestResult]   = useState(null);
  const [testing, setTesting]         = useState(false);

  const rulesTabRef = useRef(null);
  const mappingTabRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, rls, mappings] = await Promise.all([
        api.request('/admin/access-control/config'),
        api.request('/admin/access-rules'),
        api.request('/admin/role-mapping-rules'),
      ]);
      setConfig(cfg);
      setRules(rls);
      setMappingRules(mappings);
    } catch (e) {
      toast.error('Failed to load access control data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Access Control toggle ──────────────────────────────────────────────────
  const toggleEnabled = async () => {
    setToggling(true);
    try {
      const newVal = !config.enabled;
      await api.request('/admin/access-control/config', { method: 'PUT', body: JSON.stringify({ enabled: newVal }) });
      setConfig(c => ({ ...c, enabled: newVal }));
      toast.success(`Access control ${newVal ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update config'); }
    finally { setToggling(false); }
  };

  // ── Test token ─────────────────────────────────────────────────────────────
  const runTest = async () => {
    setTesting(true); setTestResult(null);
    const props = { ...testProps };
    if (props.email && !props.domain) props.domain = props.email.split('@')[1] || '';
    try {
      const result = await api.request('/admin/access-rules/test-token', { method: 'POST', body: JSON.stringify({ token_props: props }) });
      setTestResult(result);
    } catch { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  const handleHeaderAddClick = () => {
    if (activeTab === 'access') rulesTabRef.current?.openCreate();
    else if (activeTab === 'rolemapping') mappingTabRef.current?.openCreateMapping();
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-10 h-10 border-4 border-levelup-accent border-t-transparent rounded-full" />
    </div>
  );

  const allowRules = rules.filter(r => r.effect === 'ALLOW' && r.is_active);

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck size={28} className="text-levelup-accent" />
            Access Control
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Control who can access the portal and what role they receive via LevelUp SSO.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="test-token-btn"
            onClick={() => setShowTest(s => !s)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <FlaskConical size={16} /> Test Token
          </button>
          {(activeTab === 'access' || activeTab === 'rolemapping') && (
          <button
            data-testid={activeTab === 'access' ? 'create-rule-btn' : 'create-mapping-btn'}
            onClick={handleHeaderAddClick}
            className="flex items-center gap-2 px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg text-sm font-bold hover:opacity-90 transition-opacity"
          >
            <Plus size={16} /> {activeTab === 'access' ? 'Add Rule' : 'Add Mapping'}
          </button>
          )}
        </div>
      </div>

      {/* Master Toggle */}
      <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
        config.enabled ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center gap-3">
          <ShieldCheck size={22} className={config.enabled ? 'text-emerald-600' : 'text-gray-400'} />
          <div>
            <p className="font-semibold text-gray-800">
              Access Control is <span className={config.enabled ? 'text-emerald-600' : 'text-gray-500'}>
                {config.enabled ? 'ENABLED' : 'DISABLED'}
              </span>
            </p>
            <p className="text-xs text-gray-500">
              {config.enabled
                ? 'SSO logins are evaluated against your rules below.'
                : 'All LevelUp SSO users can log in (no rules enforced).'}
            </p>
          </div>
        </div>
        <button
          data-testid="toggle-access-control"
          onClick={toggleEnabled}
          disabled={toggling}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all"
          style={{ background: config.enabled ? '#dc2626' : '#0D3B36', color: 'white' }}
        >
          {config.enabled ? <ToggleRight size={18}/> : <ToggleLeft size={18}/>}
          {config.enabled ? 'Disable' : 'Enable'}
        </button>
      </div>

      {/* Warning when enabled but no allow rules */}
      {config.enabled && allowRules.length === 0 && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-red-800">Critical: All SSO logins are currently being denied</p>
            <p className="text-sm text-red-700 mt-1">
              Access control is <strong>enabled</strong> but you have <strong>no active ALLOW rules</strong>.
              Every user who tries to sign in via LevelUp SSO will see "Authorisation Required" and be blocked.
            </p>
            <p className="text-sm text-red-600 mt-2">
              <strong>Quick fix:</strong> Click "Add Rule" above, set effect to <strong>ALLOW</strong>,
              add a condition like <code className="bg-red-100 px-1 rounded">domain</code> <code className="bg-red-100 px-1 rounded">in</code>{' '}
              with your allowed partner domains (e.g. <code className="bg-red-100 px-1 rounded">microsoft.com, testpartner.com</code>),
              then save and enable it.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          data-testid="tab-access-rules"
          onClick={() => setActiveTab('access')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'access'
              ? 'border-levelup-dark text-levelup-dark'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <ShieldCheck size={16} /> Access Rules
          <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{rules.length}</span>
        </button>
        <button
          data-testid="tab-role-mapping"
          onClick={() => setActiveTab('rolemapping')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'rolemapping'
              ? 'border-levelup-dark text-levelup-dark'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users size={16} /> Role Mapping
          <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{mappingRules.length}</span>
        </button>
        <button
          data-testid="tab-sso-log"
          onClick={() => setActiveTab('sso-log')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'sso-log'
              ? 'border-levelup-dark text-levelup-dark'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <AlertTriangle size={16} /> SSO Attempts Log
        </button>
        <button
          data-testid="tab-integrations"
          onClick={() => setActiveTab('integrations')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'integrations'
              ? 'border-levelup-dark text-levelup-dark'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Plug size={16} /> Integrations
        </button>
      </div>

      {/* Test Panel */}
      {showTest && (
        <div data-testid="test-panel" className="p-5 bg-white border border-gray-200 rounded-xl shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FlaskConical size={16} /> Test Token Against Access Rules
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            {['email', 'user_role', 'domain_type', 'country'].map(field => (
              <div key={field}>
                <label className="text-xs font-medium text-gray-600 block mb-1 capitalize">
                  {field.replace('_', ' ')}
                </label>
                <input
                  data-testid={`test-${field}`}
                  type="text"
                  value={testProps[field] || ''}
                  onChange={e => setTestProps(p => ({ ...p, [field]: e.target.value }))}
                  placeholder={config.token_fields.find(f => f.field === field)?.example || ''}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-levelup-accent"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mb-3">
            <button
              data-testid="run-test-btn"
              onClick={runTest}
              disabled={testing}
              className="px-4 py-2 bg-levelup-darker text-white rounded-lg text-sm font-medium disabled:opacity-60"
            >
              {testing ? 'Testing...' : 'Run Test'}
            </button>
            {testResult && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium flex-1 ${
                testResult.allowed ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
              }`}>
                {testResult.allowed ? <Check size={16}/> : <X size={16}/>}
                <span>{testResult.reason}</span>
              </div>
            )}
          </div>
          {/* Rule Breakdown */}
          {testResult?.rule_breakdown?.length > 0 && (
            <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">Rule Evaluation Breakdown</div>
              {testResult.rule_breakdown.map((rb, i) => (
                <div key={i} className={`px-3 py-2 border-t border-gray-100 ${rb.matched ? 'bg-emerald-50' : 'bg-white'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${rb.matched ? 'bg-emerald-500' : 'bg-gray-300'}`}/>
                    <span className="text-sm font-medium text-gray-800">{rb.rule_name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${rb.effect === 'ALLOW' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{rb.effect}</span>
                    <span className="text-xs text-gray-400 ml-auto">{rb.match_all ? 'AND' : 'OR'}</span>
                    <span className={`text-xs font-semibold ${rb.matched ? 'text-emerald-600' : 'text-gray-400'}`}>{rb.matched ? '✓ MATCHED' : '✗ no match'}</span>
                  </div>
                  <div className="pl-4 space-y-0.5">
                    {rb.conditions.map((c, j) => (
                      <div key={j} className="flex items-center gap-1 text-xs">
                        <span className={c.result ? 'text-emerald-600' : 'text-red-500'}>{c.result ? '✓' : '✗'}</span>
                        <span className="text-gray-500">{c.field}</span>
                        <span className="text-gray-400">{c.operator}</span>
                        <span className="font-mono bg-gray-100 px-1 rounded">{c.value}</span>
                        <span className="text-gray-400">→ token has:</span>
                        <span className={`font-mono px-1 rounded ${c.result ? 'bg-emerald-100' : 'bg-red-50'}`}>{c.token_value || '(empty)'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB BODIES ─────────────────────────────────────────────────────── */}
      {activeTab === 'access' && (
        <AccessRulesTab ref={rulesTabRef} rules={rules} setRules={setRules} config={config} reload={load} />
      )}

      {activeTab === 'rolemapping' && (
        <RoleMappingTab ref={mappingTabRef} mappingRules={mappingRules} setMappingRules={setMappingRules} config={config} reload={load} />
      )}

      {activeTab === 'sso-log' && (
        <SsoAttemptsLogTab />
      )}

      {activeTab === 'integrations' && (
        <IntegrationsTab onViewSsoLog={() => setActiveTab('sso-log')} />
      )}
    </div>
  );
}
